import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookingSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("success");
  const [paymentData, setPaymentData] = useState(null);
  const bookingId = searchParams.get("booking_id");

  // Format date to DD-MM-YYYY
  const formatDateDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (!bookingId) {
        setStatus("error");
        return;
      }

      try {
        setStatus("loading");
        // Fetch booking details
        const res = await axios.get(`${API}/bookings/${bookingId}`);
        console.log("Booking details:", res.data);
        setPaymentData(res.data);

        if (res.data.payment_status === "paid") {
          setStatus("success");
        } else {
          setStatus("error");
        }
      } catch (error) {
        console.error("Error fetching booking details:", error);
        setStatus("error");
      }
    };

    fetchBookingDetails();
  }, [bookingId]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="booking-success-page">
      <Header />
      
      <div className="pt-32 pb-20 px-4">
        <div className="max-w-lg mx-auto text-center">
          {status === "loading" && (
            <div data-testid="loading-state">
              <Loader2 className="w-16 h-16 text-[#E67E22] animate-spin mx-auto mb-6" />
              <h1 className="font-serif text-2xl font-bold text-[#2C3E50] mb-4">
                Processing Your Payment
              </h1>
              <p className="text-[#7F8C8D]">
                Please wait while we confirm your booking...
              </p>
            </div>
          )}

          {status === "success" && (
            <div data-testid="success-state">
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
              <h1 className="font-serif text-3xl font-bold text-[#2C3E50] mb-4">
                Booking Confirmed!
              </h1>
              <p className="text-[#7F8C8D] mb-6">
                Thank you for your booking. A confirmation email will be sent to your registered email address.
              </p>
              {paymentData && (
                <div className="bg-white rounded-lg p-6 shadow mb-6 text-left">
                  <h3 className="font-semibold text-[#2C3E50] mb-4">Booking Details</h3>
                  <div className="space-y-2 text-sm text-[#7F8C8D]">
                    <p><strong>Booking No:</strong> {paymentData.booking_number}</p>
                    <p><strong>Guest Name:</strong> {paymentData.guest_name}</p>
                    <p><strong>Check-in:</strong> {formatDateDisplay(paymentData.check_in_date)}</p>
                    <p><strong>Check-out:</strong> {formatDateDisplay(paymentData.check_out_date)}</p>
                    <p><strong>Total Amount:</strong> ₹{paymentData.total_amount?.toLocaleString()}</p>
                    <p><strong>Payment Status:</strong> <span className="text-green-600 font-medium">Paid</span></p>
                    <p><strong>Booking Status:</strong> <span className="text-green-600 font-medium capitalize">{paymentData.booking_status}</span></p>
                  </div>
                </div>
              )}
              <Link
                to="/"
                className="btn-primary inline-block"
                data-testid="back-home-btn"
              >
                Back to Home
              </Link>
            </div>
          )}

          {(status === "error" || status === "timeout" || status === "expired") && (
            <div data-testid="error-state">
              <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
              <h1 className="font-serif text-3xl font-bold text-[#2C3E50] mb-4">
                {status === "expired" ? "Session Expired" : "Something Went Wrong"}
              </h1>
              <p className="text-[#7F8C8D] mb-6">
                {status === "expired" 
                  ? "Your payment session has expired. Please try booking again."
                  : status === "timeout"
                  ? "Payment verification timed out. Please check your email for confirmation."
                  : "We couldn't verify your payment. Please contact support if you were charged."}
              </p>
              <div className="space-x-4">
                <Link
                  to="/bookings"
                  className="btn-primary inline-block"
                  data-testid="try-again-btn"
                >
                  Try Again
                </Link>
                <Link
                  to="/contact"
                  className="btn-secondary inline-block"
                  data-testid="contact-support-btn"
                >
                  Contact Support
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
